import React, { useState } from "react";
import { createTipCheckout } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";

const PRESETS = [1, 3, 5, 10, 25];

/**
 * TipButton — small CTA on player cards. Opens TipModal which fires a
 * one-shot Stripe checkout via /api/tips/checkout. Hidden when:
 *   - the viewer is the same user
 *   - the global `tips_enabled` feature flag is OFF (admin hasn't
 *     activated creator payouts yet — keeps the surface dark until
 *     wallets + withdrawals are go-live ready)
 */
export function TipButton({ recipient, onToast, compact }) {
  const { user } = useAuth();
  const { flags } = useFeatureFlags();
  const [open, setOpen] = useState(false);
  if (!flags?.tips_enabled) return null;
  if (!user || !recipient || user.user_id === recipient.user_id) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`tip-btn-${recipient.user_id}`}
        title={`Tip ${recipient.name || "this operator"}`}
        className={`inline-flex items-center gap-1 ${
          compact ? "px-2 py-1 text-[10px]" : "px-3 py-1.5 text-[11px]"
        } tracking-widest font-bold rounded-xl bg-gradient-to-r from-[#ffe066] to-[#ff8a3a] text-black hover:brightness-110`}
      >
        <i className="fas fa-coins" />TIP
      </button>
      {open && (
        <TipModal
          recipient={recipient}
          onClose={() => setOpen(false)}
          onToast={onToast}
        />
      )}
    </>
  );
}

function TipModal({ recipient, onClose, onToast }) {
  const [amount, setAmount] = useState(5);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await createTipCheckout(recipient.user_id, amount, message.trim());
      if (r?.url) {
        window.location.href = r.url;
        return;
      }
      onToast?.("Couldn't open checkout.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't start tip checkout.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="tip-modal"
    >
      <div
        className="rounded-3xl p-6 w-full max-w-sm border-2 border-[#ffe066]/60 bg-gradient-to-br from-[#1c1505] via-[#2a1a05] to-[#1c1505]"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[10px] tracking-[4px] text-[#ffe066] font-bold">
          <i className="fas fa-coins mr-1" />TIP {recipient.name?.toUpperCase()}
        </p>
        <p className="text-[12px] text-white/65 mt-1">
          Show love for a clutch play or a great host. One-time charge — your card is never stored.
        </p>

        <div className="mt-4 grid grid-cols-5 gap-2" data-testid="tip-amount-grid">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setAmount(p)}
              data-testid={`tip-amount-${p}`}
              className={`py-2 rounded-xl text-sm font-bold border ${
                amount === p
                  ? "bg-[#ffe066] text-black border-[#ffe066]"
                  : "bg-black/30 text-white/80 border-white/15 hover:border-[#ffe066]/40"
              }`}
            >
              ${p}
            </button>
          ))}
        </div>

        <label className="block mt-4">
          <span className="text-[10px] tracking-widest text-[#ffe066]/70">MESSAGE (OPTIONAL)</span>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={240}
            placeholder="GG, that ace was unreal"
            data-testid="tip-message-input"
            className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm placeholder-white/30"
          />
        </label>

        <div className="mt-5 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="tip-cancel"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="tip-checkout-btn"
            className="text-[11px] tracking-widest px-5 py-2 rounded-xl bg-gradient-to-r from-[#ffe066] to-[#ff8a3a] text-black font-bold hover:brightness-110 disabled:opacity-60"
          >
            {busy ? "…" : `SEND $${amount}`}
          </button>
        </div>
      </div>
    </div>
  );
}
